/**
 * Builds a renderable `data:` URL from a stored app icon.
 *
 * Icons are persisted as raw base64 of whatever asset the binary shipped — device-service pulls
 * the largest matching raster out of an APK, which may be a PNG, a WebP or a JPEG. Call sites used
 * to hardcode `image/png`, so every WebP icon (the Android Studio default since 2017) silently
 * failed to render. The media type is therefore sniffed from the leading magic bytes.
 */

/** Enough base64 to cover the first 18 bytes; the length stays a multiple of 4 so it decodes cleanly. */
const MAGIC_B64_CHARS = 24;

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0): boolean =>
  signature.every((byte, index) => bytes[offset + index] === byte);

const asciiAt = (bytes: Uint8Array, offset: number, text: string): boolean =>
  startsWith(
    bytes,
    [...text].map(character => character.charCodeAt(0)),
    offset,
  );

const detectMediaType = (bytes: Uint8Array): string => {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47])) return 'image/png';
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  // WebP is a RIFF container; the format tag sits at byte 8
  if (asciiAt(bytes, 0, 'RIFF') && asciiAt(bytes, 8, 'WEBP')) return 'image/webp';
  if (asciiAt(bytes, 0, 'GIF8')) return 'image/gif';
  // fall back to the historical assumption rather than refusing to render
  return 'image/png';
};

export const appIconDataUrl = (iconBase64?: string | null): string => {
  if (!iconBase64) return '';
  // tolerate a value that is already a full data URL
  if (iconBase64.startsWith('data:')) return iconBase64;

  let mediaType = 'image/png';
  try {
    const header = window.atob(iconBase64.slice(0, MAGIC_B64_CHARS));
    const bytes = Uint8Array.from(header, character => character.charCodeAt(0));
    mediaType = detectMediaType(bytes);
  } catch {
    // malformed base64 — emit the URL anyway and let the <img> fall back
  }

  return `data:${mediaType};base64,${iconBase64}`;
};
